import { analyzeWellness } from "./wellnessAnalysis.js";
import { analyzeAngerPatterns } from "./angerAnalysis.js";
import { analyzeGad7 } from "./gad7Analysis.js";
import { getInsightCombinedText } from "./mentalPatternAnalysis.js";
import { getReportingThinkingPattern } from "./confirmedEmotion.js";
import {
  MENTAL_LOOPS,
  getPatternSummaryCopy,
  formatStressTrendLabel
} from "../constants/mentalPatternsHub.js";
import { MENTAL_PATTERN_LABELS } from "../constants/mentalPatterns.js";
import { formatLockedPatternTeaseMessage } from "../constants/patternTease.js";
import { CBT_PATTERN_LEARN_MORE } from "../constants/cbtPatternLearnMore.js";

// Re-export parseInsightDate if not exported — check mentalPatternAnalysis
function getInsightDate(insight) {
  const raw = insight.created_at != null ? insight.created_at : insight.createdAt;
  const d = raw ? new Date(raw) : new Date();
  return Number.isNaN(d.getTime()) ? new Date() : d;
}

function insightsInAgeRange(insights, minDays, maxDays) {
  const now = Date.now();
  const msDay = 86400000;
  return (insights || []).filter((item) => {
    const age = (now - getInsightDate(item).getTime()) / msDay;
    return age >= minDays && age < maxDays;
  });
}

function trendDirection(delta, threshold = 3) {
  if (Math.abs(delta) < threshold) return "stable";
  return delta > 0 ? "up" : "down";
}

/**
 * Wellness week-over-week trend from reflection subsets.
 * Replace with API trend series when available.
 */
function computeWellnessTrend(insights) {
  const thisWeek = insightsInAgeRange(insights, 0, 7);
  const lastWeek = insightsInAgeRange(insights, 7, 14);

  if (!thisWeek.length && !lastWeek.length) {
    return { direction: "stable", label: "→ Building your baseline" };
  }

  const scoreNow = analyzeWellness(thisWeek).score;
  const scorePrev = analyzeWellness(lastWeek).score;

  if (scoreNow == null && scorePrev == null) {
    return { direction: "stable", label: "→ Building your baseline" };
  }
  if (scorePrev == null || lastWeek.length === 0) {
    return { direction: "stable", label: "→ Tracking this week" };
  }
  if (thisWeek.length === 0) {
    return { direction: "stable", label: "→ No reflections this week" };
  }

  const delta = scoreNow - scorePrev;
  const pct = scorePrev > 0 ? Math.round((Math.abs(delta) / scorePrev) * 100) : Math.abs(delta);
  const dir = trendDirection(delta, 4);

  if (dir === "stable") return { direction: "stable", label: "→ Steady this week" };
  if (dir === "down") return { direction: "down", label: `↓ ${pct}% this week` };
  return { direction: "up", label: `↑ ${pct}% this week` };
}

/**
 * Compare dominant stress pattern counts: last 7 days vs previous 7 days.
 */
function computeStressTrend(stressAnalysis, insights) {
  const { attentionPattern, distribution } = stressAnalysis;
  const pattern = attentionPattern || "overthinking";

  let thisCount = 0;
  let lastCount = 0;

  // Count stress reflections per pattern in each window using stressInsights dates
  const stressList = stressAnalysis.stressInsights || [];
  const now = Date.now();
  const msDay = 86400000;

  stressList.forEach((entry) => {
    const age = (now - new Date(entry.date).getTime()) / msDay;
    if (age < 7) {
      if (entry.pattern === pattern) thisCount += 1;
    } else if (age >= 7 && age < 14) {
      if (entry.pattern === pattern) lastCount += 1;
    }
  });

  if (thisCount === 0 && lastCount === 0 && (distribution[pattern] || 0) > 0) {
    return { direction: "up", label: formatStressTrendLabel(pattern) };
  }

  const delta = thisCount - lastCount;
  const dir = trendDirection(delta, 1);

  if (dir === "stable") {
    const name = MENTAL_PATTERN_LABELS[pattern] || "Pattern";
    return { direction: "stable", label: `→ ${name} steady` };
  }
  if (dir === "up") return { direction: "up", label: formatStressTrendLabel(pattern) };
  const name = MENTAL_PATTERN_LABELS[pattern] || "Pattern";
  return { direction: "down", label: `↓ ${name} easing` };
}

/**
 * Healthy anger score week-over-week.
 */
function computeAngerTrend(insights) {
  const thisWeek = insightsInAgeRange(insights, 0, 7);
  const lastWeek = insightsInAgeRange(insights, 7, 14);

  const angerNow = analyzeAngerPatterns(thisWeek);
  const angerPrev = analyzeAngerPatterns(lastWeek);

  if (!angerNow.totalAnger && !angerPrev.totalAnger) {
    return { direction: "stable", label: "→ Stable pattern" };
  }
  if (!angerPrev.totalAnger) {
    return { direction: "stable", label: "→ Tracking anger patterns" };
  }
  if (!angerNow.totalAnger) {
    return { direction: "stable", label: "→ Stable pattern" };
  }

  const delta = angerNow.healthyAngerScore - angerPrev.healthyAngerScore;
  const dir = trendDirection(delta, 5);

  if (dir === "stable") return { direction: "stable", label: "→ Stable pattern" };
  if (dir === "up") return { direction: "up", label: "↑ Deliberate anger rising" };
  return { direction: "down", label: "↓ More reactive anger lately" };
}

/**
 * GAD-7 signal week-over-week (last 7 days vs previous 7 days).
 */
function computeGad7Trend(insights) {
  const thisWeek = insightsInAgeRange(insights, 0, 7);
  const lastWeek = insightsInAgeRange(insights, 7, 14);

  const gad7Now = analyzeGad7(thisWeek);
  const gad7Prev = analyzeGad7(lastWeek);

  if (!gad7Now.hasData && !gad7Prev.hasData) {
    return { direction: "stable", label: "→ Building your baseline" };
  }
  if (!gad7Prev.hasData) {
    return { direction: "stable", label: "→ Tracking this week" };
  }
  if (!gad7Now.hasData) {
    return { direction: "down", label: "↓ Anxiety signal easing" };
  }

  const delta = gad7Now.totalScore - gad7Prev.totalScore;
  const dir = trendDirection(delta, 2);

  if (dir === "stable") return { direction: "stable", label: "→ Steady this week" };
  if (dir === "up") return { direction: "up", label: "↑ Anxiety signal rising" };
  return { direction: "down", label: "↓ Anxiety signal easing" };
}

/**
 * TODO: replace with server-side loop clustering when API exists.
 * Combines text-signal loops with confirmed Quick Clarity CBT patterns.
 */
function detectMentalLoops(insights) {
  const list = Array.isArray(insights) ? insights : [];
  const textDetected = MENTAL_LOOPS.map((loop) => {
    let hits = 0;
    for (const item of list) {
      const text = getInsightCombinedText(item);
      if (!text) continue;
      for (const re of loop.signals) {
        if (re.test(text)) {
          hits += 1;
          break;
        }
      }
    }
    return { ...loop, hits, source: "text" };
  }).filter((l) => l.hits > 0);

  const cbtCounts = {};
  for (const item of list) {
    const pattern = getReportingThinkingPattern(item);
    if (!pattern || pattern.kind !== "cbt") continue;
    if (!cbtCounts[pattern.key]) {
      cbtCounts[pattern.key] = { key: pattern.key, label: pattern.label, hits: 0 };
    }
    cbtCounts[pattern.key].hits += 1;
  }

  const cbtDetected = Object.values(cbtCounts)
    .filter((row) => row.hits > 0)
    .map((row) => {
      const learn = CBT_PATTERN_LEARN_MORE[row.key];
      return {
        id: `cbt_${row.key}`,
        title: row.label,
        description: learn?.definition || "A thinking pattern confirmed in your Quick Clarity reflections.",
        hits: row.hits,
        source: "cbt"
      };
    });

  const detected = [...cbtDetected, ...textDetected].sort((a, b) => b.hits - a.hits);

  if (detected.length) {
    return detected.map(({ id, title, description, hits, source }) => ({
      id,
      title,
      description,
      hits,
      source
    }));
  }

  // Placeholder: show signature loops when user has reflections but no strong signal yet
  if (list.length > 0) {
    return MENTAL_LOOPS.map(({ id, title, description }) => ({
      id,
      title,
      description,
      hits: 0,
      placeholder: true
    }));
  }
  return [];
}

export function buildPatternInsightSummary(stressAnalysis, wellness) {
  const pattern = stressAnalysis.attentionPattern || stressAnalysis.latestPattern || "overthinking";
  const copy = getPatternSummaryCopy(pattern);

  return {
    dominantPattern: pattern,
    patternLabel: MENTAL_PATTERN_LABELS[pattern] || "Overthinking",
    title: "Summarized Insight",
    body: copy.body,
    focus: copy.focus,
    wellnessScore: wellness.score
  };
}

function countRepeatingLoops(loops, minHits) {
  if (!Array.isArray(loops) || !loops.length) return 0;
  return loops.filter((loop) => !loop.placeholder && loop.hits >= minHits).length;
}

function countRecurringInterpretations(insights, minHits) {
  const counts = {};
  for (const item of insights || []) {
    const reported = getReportingThinkingPattern(item);
    if (reported?.kind === "cbt") {
      counts[reported.key] = (counts[reported.key] || 0) + 1;
      continue;
    }
    const pattern = item.insight_json?.behavior_pattern;
    if (!pattern || typeof pattern !== "string") continue;
    const label = pattern.trim();
    if (!label) continue;
    counts[label] = (counts[label] || 0) + 1;
  }
  return Object.values(counts).filter((hits) => hits >= minHits).length;
}

/**
 * Personalized counts for free-tier locked pattern tease (full history, reveal hidden).
 */
export function buildLockedPatternTease(insights, hubInsights) {
  const list = Array.isArray(insights) ? insights : [];
  const totalReflections = list.length;
  const loops = hubInsights?.loops || [];

  let loopCount = countRepeatingLoops(loops, 2);
  let interpretationCount = countRecurringInterpretations(list, 2);

  if (loopCount === 0) loopCount = countRepeatingLoops(loops, 1);
  if (interpretationCount === 0) interpretationCount = countRecurringInterpretations(list, 1);

  const hasTeaseableData = loopCount > 0 || interpretationCount > 0;
  const message = formatLockedPatternTeaseMessage(loopCount, interpretationCount, totalReflections);

  return {
    loopCount,
    interpretationCount,
    totalReflections,
    message,
    hasTeaseableData
  };
}

export function buildMentalPatternsHubInsights(insights, stressAnalysis, wellness, angerAnalysis, gad7Analysis) {
  const list = Array.isArray(insights) ? insights : [];

  return {
    summary: buildPatternInsightSummary(stressAnalysis, wellness),
    trends: {
      wellness: computeWellnessTrend(list),
      stress: computeStressTrend(stressAnalysis, list),
      anger: computeAngerTrend(list),
      gad7: computeGad7Trend(list)
    },
    loops: detectMentalLoops(list),
    suggestedPrompt:
      "What fact are you emotionally interpreting without enough evidence?",
    hasAngerData: (angerAnalysis?.totalAnger || 0) > 0,
    hasStressData: (stressAnalysis?.totalStress || 0) > 0,
    hasGad7Data: gad7Analysis?.hasData || false
  };
}
